import { IonSpinner } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { MapPinOff } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { useAddressCapture } from '../../hooks/useAddressCapture';
import { useAutoCaptureOnPermissionGrant } from '../../hooks/useAutoCaptureOnPermissionGrant';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Explainer shown when a user taps the "no location" pin on a dish card.
 * Distance is only ever displayed for device-sourced locations (see
 * useLocationStore.LocationSource), so the single action here is to enable
 * device geolocation. A successful capture stamps the location source as
 * `'device'`, which unlocks distances app-wide, and we close.
 *
 * Deliberately device-only — no ZIP fallback, since a ZIP/centroid would not
 * re-enable distance. Owns one `useAddressCapture` instance at the page level
 * so a long feed of cards doesn't each instantiate the capture mutations.
 */
export const DistanceLocationGateModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('location');
  const { geoLoading, geoError, handleUseDeviceLocation } = useAddressCapture({
    onCaptured: onClose,
  });

  // While the popup is up, granting permission via the native prompt / site
  // settings (not just the button) captures through this same flow and closes
  // the popup — no second click needed. Scoped to `isOpen` so a grant while the
  // user is elsewhere in the app never silently moves their browse location.
  useAutoCaptureOnPermissionGrant(isOpen, handleUseDeviceLocation);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" className="space-y-4">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-100 text-muted-foreground">
          <MapPinOff className="h-6 w-6" aria-hidden="true" />
        </div>
        <p className="text-sm text-muted-foreground">{t('distanceGate.body')}</p>
      </div>

      <button
        type="button"
        onClick={handleUseDeviceLocation}
        disabled={geoLoading}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary py-3 text-base font-semibold text-white active:opacity-80 disabled:opacity-50"
      >
        {geoLoading ? (
          <>
            <IonSpinner name="crescent" className="h-5 w-5" />
            {t('gettingLocation')}
          </>
        ) : (
          t('distanceGate.enableCta')
        )}
      </button>
      {geoError && (
        <p className="text-center text-sm text-brand-danger">{t('distanceGate.error')}</p>
      )}
    </Modal>
  );
};
